import { AccordionTabChangeEvent } from 'primereact/accordion';
import { useEffect, useMemo, useState } from 'react';
import WorkLocation, { InitialWorkLocation } from './WorkLocation';
import { Accordion } from '@design-system';
import { useGetUserContacts } from 'src/shared/queries/useGetUserContacts';
import { AddressType, ContactAddress } from 'src/shared/types/User';
import { useGetCorporateLocations } from 'src/shared/queries/useGetCorporateLocations';
import { CheckIsEmptyAddressItem, processUpdatedDetails } from 'src/pages/UserProfile/UserProfile.service';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { useUserProfileStore } from 'src/stores/UserProfile';

const AddressFields = [
  'workLocationType',
  'country',
  'addressLine1',
  'addressLine2',
  'city',
  'unitType',
  'suite',
  'state',
  'zipCode',
  'county',
  'startDate',
  'endDate',
];

const WorkLocations = () => {
  const { data: userProfile } = useGetUser();
  const userId = userProfile?.userId;
  const { profileInfo: selectedProfile, editMode, updatedFields, setUpdatedFields } = useUserProfileStore();
  const isPrincipal = selectedProfile?.isPrincipal;
  const { data: userContacts } = useGetUserContacts(userId, isPrincipal);
  const { data: corporateLocations } = useGetCorporateLocations(userProfile?.companyId ?? 0);
  const [workLocations, setWorkLocations] = useState<ContactAddress[]>([]);
  const [activeIndexes, setActiveIndexes] = useState<number[]>([0]);

  const memorizedWorkLocations = useMemo(() => {
    const list = userContacts?.contactAddresses?.filter((address) => address.addressType === AddressType.WORK) || [];
    return list;
  }, [userContacts?.contactAddresses]);

  useEffect(() => {
    const list = userContacts?.contactAddresses?.filter((address) => address.addressType === AddressType.WORK) || [];
    if (list.length > 0) {
      setWorkLocations([...list]);
    }
  }, [userContacts?.contactAddresses]);

  useEffect(() => {
    if (editMode && userContacts && workLocations) {
      const updatedAddresses = processUpdatedDetails(
        workLocations.map((address) => ({ ...address, addressType: AddressType.WORK })),
        memorizedWorkLocations.map((address) => ({ ...address })),
        { contactRefId: userContacts?.contactRefId ?? 0 },
        'contactAddressId',
        AddressFields
      );
      const newFields = { ...updatedFields };
      const hasUpdatedAddresses = Object.values(updatedAddresses).some((list) => list?.length > 0);
      if (hasUpdatedAddresses) {
        newFields.contactAddresses = updatedAddresses;
      } else {
        delete newFields.contactAddresses;
      }
      setUpdatedFields(Object.keys(newFields)?.length > 0 ? newFields : null);
    }
  }, [editMode, userContacts, workLocations]);

  const onChange = (index: number) => (location: ContactAddress) => {
    setWorkLocations((prevLocations) => {
      return prevLocations.map((item, previousIndex) => (previousIndex === index ? location : item));
    });
  };

  const handleAddAnotherWorkLocation = () => {
    setWorkLocations((prevState) => [...prevState, { ...InitialWorkLocation }]);
    setActiveIndexes([workLocations.length]);
  };

  const handleDeleteWorkLocation = async (index: number) => {
    setWorkLocations((prevLocations) => {
      const updatedLocations = prevLocations.filter((_, prevIndex) => prevIndex !== index);
      if (updatedLocations.length === 1) {
        setActiveIndexes([0]);
      }
      return updatedLocations;
    });
  };

  const handleTabChange = (e: AccordionTabChangeEvent) => {
    setActiveIndexes(e.index as number[]);
  };

  const isAddAnotherDisabled = !editMode || workLocations.some(CheckIsEmptyAddressItem);

  const workLocationTabs = userContacts?.contactRefId
    ? workLocations.map((location, index) => {
        const refLocation = memorizedWorkLocations.find(
          (item) => item.contactAddressId === location.contactAddressId
        ) as ContactAddress;
        return {
          body: (
            <WorkLocation
              refLocation={refLocation}
              workLocation={location}
              corporateLocations={corporateLocations || []}
              onChange={onChange(index)}
              disabled={!editMode}
            />
          ),
        };
      })
    : [];

  return (
    <Accordion
      multiple
      allowToDelete={true}
      isDeleteDisabled={!editMode}
      activeIndex={activeIndexes}
      shouldPreserveFirstTab
      tabs={workLocationTabs}
      onTabChange={handleTabChange}
      onDeleteTab={handleDeleteWorkLocation}
      tabHeaderPrefix="WORK LOCATIONS"
      additionalTabConfig={{
        buttonLabel: 'WORK LOCATION',
        onAdd: handleAddAnotherWorkLocation,
        disableAdd: isAddAnotherDisabled,
      }}
    />
  );
};

export default WorkLocations;
